/**
 * connectFunnelAppender.ts — the production sink for the connect funnel
 * (plan P0 #8 / §9.37, 2026-09-02).
 *
 * `setConnectFunnelSink` had ZERO callers, so none of the up-and-go budget
 * (§3.8) was measurable — every hop was folklore. This appender is the
 * interim sink until the connect-intent record's typed history[] (P1a) is
 * the durable one: it batches every logConnectFunnel record, flushes every
 * 5 s and on pagehide (keepalive), and POSTs the batch with the Firebase
 * bearer to the composioApi `connect-funnel` case, which folds it into
 * `ops/connectFunnel/days/{day}` with the Admin SDK (ops/** is server-only
 * by rules — the client never writes Firestore here).
 *
 * Telemetry is never load-bearing: the queue is capped (oldest dropped),
 * records still queued while signed out are dropped at flush, a failed
 * POST is dropped (no retry storms), and nothing here can throw into the
 * flow that logged the event. The dev-visible console.debug of the default
 * sink stays.
 */
import { getAuth } from 'firebase/auth';
import { setConnectFunnelSink, type ConnectFunnelRecord } from './connectPopupFlow';

const API_BASE = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';
export const CONNECT_FUNNEL_ENDPOINT = `${API_BASE}/composioApi/composio/connect-funnel`;
/** Newest-wins cap on the unsent queue (one batch = one POST). */
export const CONNECT_FUNNEL_QUEUE_CAP = 50;
export const CONNECT_FUNNEL_FLUSH_MS = 5_000;
const DETAIL_CAP = 200;

export interface QueuedFunnelRecord {
  event: string;
  toolkitSlug?: string;
  surface?: string;
  detail?: string;
  ms?: number;
  /** Client wall-clock at emit (ms epoch) — the server keeps it as sample time. */
  at: number;
  /** The uid signed in when this row was LOGGED. Stripped before the POST —
   *  the server reads identity from the bearer — it exists only so a flush
   *  can refuse to attribute a row to someone who did not perform it. */
  uid?: string;
}

let queue: QueuedFunnelRecord[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let uninstall: (() => void) | null = null;
/** The last bearer a timed flush used — the pagehide flush reuses it
 *  synchronously so the keepalive POST never waits on a token round trip. */
let cachedToken: string | null = null;
/** The uid that bearer was minted for. The appender is installed ONCE for the
 *  tab's lifetime, so a sign-out/sign-in in the same tab (no reload) would
 *  otherwise let a pagehide flush post the NEW user's queued rows under the
 *  PREVIOUS user's bearer — the server resolves uid/accountId from the token,
 *  so those rows would land on the wrong account (2026-09-02 review). */
let cachedTokenUid: string | null = null;

/** Who is signed in right now, or null (signed out, or auth still resolving). */
function currentUid(): string | null {
  try {
    return getAuth().currentUser?.uid ?? null;
  } catch {
    return null;
  }
}

function toQueued(record: ConnectFunnelRecord, uid: string | null): QueuedFunnelRecord {
  return {
    event: String(record.event || '').slice(0, 40),
    ...(record.toolkitSlug ? { toolkitSlug: String(record.toolkitSlug).slice(0, 60) } : {}),
    ...(record.surface ? { surface: String(record.surface).slice(0, 40) } : {}),
    ...(record.detail ? { detail: String(record.detail).slice(0, DETAIL_CAP) } : {}),
    ...(typeof record.ms === 'number' && Number.isFinite(record.ms) ? { ms: Math.round(record.ms) } : {}),
    at: Date.now(),
    ...(uid ? { uid } : {}),
  };
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushConnectFunnel();
  }, CONNECT_FUNNEL_FLUSH_MS);
}

/**
 * Send everything queued. Signed out ⇒ the batch is dropped (no identity to
 * attribute it to). `keepalive` is the pagehide path: the cached bearer is
 * used as-is and the POST rides `fetch(…, { keepalive: true })`.
 */
export async function flushConnectFunnel(opts?: { keepalive?: boolean }): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  try {
    let user: { uid: string; getIdToken: () => Promise<string> } | null = null;
    try {
      user = getAuth().currentUser;
    } catch {
      user = null;
    }
    if (!user) return;
    // Identity changed in this tab ⇒ the cached bearer belongs to someone
    // else. Drop it rather than attribute this batch to them.
    if (cachedTokenUid && cachedTokenUid !== user.uid) {
      cachedToken = null;
      cachedTokenUid = null;
    }
    // Send only the rows THIS user logged (2026-09-02 review). Guarding the
    // cached bearer alone was not enough: the batch is captured before any
    // identity check, so a sign-out/sign-in inside the 5 s window handed the
    // previous user's queued rows to a freshly minted token for the new one.
    // A row logged by someone else cannot be honestly attributed to anybody
    // now — the person who performed it is gone — so it is dropped rather
    // than filed against whoever happens to be here. A row with no uid was
    // logged before auth resolved and has no identity to protect.
    const mine = batch.filter((r) => !r.uid || r.uid === user!.uid);
    if (mine.length === 0) return;
    let token = cachedToken;
    if (opts?.keepalive) {
      // Pagehide: a handler cannot rely on any async continuation running —
      // the tab tears down first — so awaiting getIdToken here would drop
      // the batch that carries the abandonment itself (2026-09-02 review).
      // With no cached bearer yet there is nothing to send synchronously:
      // put the batch back so a later flush in a surviving tab can carry it.
      if (!token) {
        queue = mine.concat(queue);
        return;
      }
    } else {
      token = await user.getIdToken();
      cachedToken = token;
      cachedTokenUid = user.uid;
    }
    if (!token || typeof fetch !== 'function') return;
    await fetch(CONNECT_FUNNEL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      // `uid` is client-side bookkeeping — the server trusts the bearer, so
      // sending it would only invite it to be believed.
      body: JSON.stringify({
        action: 'append',
        records: mine.map(({ uid: _uid, ...rest }) => rest),
      }),
      ...(opts?.keepalive ? { keepalive: true } : {}),
    });
  } catch {
    // telemetry never breaks the flow — the batch is dropped, not retried
  }
}

/**
 * Install the sink once (App.tsx bootstrap). Returns the uninstall so a
 * React effect can hand it back as its cleanup. Idempotent.
 */
export function installConnectFunnelAppender(): () => void {
  if (uninstall) return uninstall;
  setConnectFunnelSink((record) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[connect-funnel]', record.event, record);
    }
    queue.push(toQueued(record, currentUid()));
    if (queue.length > CONNECT_FUNNEL_QUEUE_CAP) {
      queue.splice(0, queue.length - CONNECT_FUNNEL_QUEUE_CAP);
    }
    scheduleFlush();
  });
  const onPageHide = () => {
    void flushConnectFunnel({ keepalive: true });
  };
  if (typeof window !== 'undefined') window.addEventListener('pagehide', onPageHide);
  uninstall = () => {
    if (typeof window !== 'undefined') window.removeEventListener('pagehide', onPageHide);
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    setConnectFunnelSink(null);
    uninstall = null;
  };
  return uninstall;
}

export function __peekConnectFunnelQueueForTests(): ReadonlyArray<QueuedFunnelRecord> {
  return queue;
}

export function __resetConnectFunnelAppenderForTests(): void {
  if (uninstall) uninstall();
  queue = [];
  cachedToken = null;
  cachedTokenUid = null;
}
